import { useMemo } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Table, THead, TBody, TR, TH, TD, ActionButton } from "components/ui/Table";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import AsyncItemSelect from "components/item/AsyncItemSelect";
import { roundAmount2Dig } from "utils/amount";
import {
  exclusiveToInclusive,
  getItemNetRate,
  withItemDirectDiscount,
  withItemDiscountPercentage,
  withSyncedRatesFromExclusive,
  withSyncedRatesFromInclusive,
} from "utils/salebillRate";
import { useBill } from "hooks/bill";

// Hard delimiter for addon table order-case values
const ADDON_VALUE_DELIMITER = "###";

export default function AddonsTable({ docType }) {
  const {
    addonTableData,
    orderCasesGrouped,
    selectBoxes,
    addItemsToTable,
    removeTableItem,
    updateTableItem,
    tableData,
    hasApplicableGst,
    companyHasGst,
  } = useBill();

  // Get vendor ID for AsyncItemSelect filtering
  const vendorId = selectBoxes.vendor?.ps_Id;

  // Create options for order-case dropdown
  const orderCaseOptions = useMemo(() => {
    return orderCasesGrouped.map((group) => ({
      value: `${group.order}${ADDON_VALUE_DELIMITER}${group.caseNumber}${ADDON_VALUE_DELIMITER}${group.lot}`,
      label: `Order: ${group.order}, Case: ${group.caseNumber} (Lot: ${group.lot})`,
      orderDetails: group,
    }));
  }, [orderCasesGrouped]);

  // Handle adding new row - just add a blank row to the table
  const handleAddRow = () => {
    const blankAddonItem = {
      selected: true, // Always true by default
      ordno: "",
      caseno: 0, // Initialize as integer
      slotqty: 1,
      sqty: 0,
      srate: 0,
      srate_incl: 0,
      dis1: 0,
      dis2perc: 0,
      dis2: 0,
      tax: 0,
      item: {
        itemid: "",
        item_full_name: "",
        aguomconval: 1,
        itmhsnId: "",
        itemuom: "",
      },
      source: selectBoxes.billType?.value || "", // Use billType value, same as regular items
      isAdditionalItems: true, // Key field
      itemDetails: [],
    };

    addItemsToTable([blankAddonItem]);
  };

  // Helper function to parse order-case value and update row
  const parseAndUpdateOrderCase = (row, orderCaseValue) => {
    if (!orderCaseValue) return row;
    const [order, caseNumber, lot] = orderCaseValue.split(ADDON_VALUE_DELIMITER);
    return {
      ...row,
      ordno: order,
      caseno: Number(caseNumber), // Convert to integer
      slotqty: Number(lot)
    };
  };

  // Don't show if no order cases available
  if (orderCasesGrouped.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <div className="sm:flex sm:items-center mb-4">
        <div className="sm:flex-auto">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Additional Items
          </h3>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <ActionButton
            onClick={handleAddRow}
            variant="primary"
            className="flex items-center justify-center"
          >
            <PlusIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Item</span>
          </ActionButton>
        </div>
      </div>

      {/* Table */}
      {addonTableData.length > 0 && (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <Table>
            <THead>
              <TR>
                <TH>Order & Case</TH>
                <TH>Item Name</TH>
                {["AGENTBILL"].includes(docType) && <TH>Rate</TH>}
                {["AGENTBILL"].includes(docType) && hasApplicableGst && <TH title="Rate including tax">Rate Incl</TH>}
                <TH>Qty</TH>
                <TH>UOM</TH>
                {["AGENTBILL"].includes(docType) && <TH>Dis Amt</TH>}
                {["AGENTBILL"].includes(docType) && <TH>Dis %</TH>}
                {["AGENTBILL"].includes(docType) && <TH>Amount</TH>}
                {companyHasGst && <TH>HSN / SAC</TH>}
                {["AGENTBILL"].includes(docType) && hasApplicableGst && <TH>Tax</TH>}
                <TH last>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {addonTableData.map((row, index) => {
                // Find current order-case value for dropdown
                const currentOrderCaseValue = row.ordno && row.caseno && row.slotqty
                  ? `${row.ordno}${ADDON_VALUE_DELIMITER}${row.caseno}${ADDON_VALUE_DELIMITER}${row.slotqty}`
                  : "";
                const currentOrderCaseOption = orderCaseOptions.find(opt => opt.value === currentOrderCaseValue);
                
                // Find current item for AsyncItemSelect
                const currentItemOption = row.item.itemid ? {
                  value: row.item.itemid,
                  label: row.item.item_full_name,
                  itemuom: row.item.itemuom,
                  conval: row.item.aguomconval,
                  itmhsnId: row.item.itmhsnId
                } : null;

                // Computed row amount after direct and percentage discounts
                const rowAmount = roundAmount2Dig(
                  (parseFloat(row.sqty) || 0) *
                    getItemNetRate(row)
                );

                const fullTableIndex = tableData.findIndex(
                  (item) => item === row && item.isAdditionalItems === true
                );

                return (
                  <TR key={`addon-${index}`}>
                    <TD>
                      <Select
                        options={orderCaseOptions}
                        value={currentOrderCaseOption}
                        onChange={(val) => {
                          const updatedRow = parseAndUpdateOrderCase(row, val?.value);
                          if (fullTableIndex !== -1) {
                            updateTableItem(fullTableIndex, updatedRow);
                          }
                        }}
                        placeholder="Select order case..."
                        isClearable
                        className="min-w-[200px]"
                      />
                    </TD>
                    
                    <TD>
                      <AsyncItemSelect
                        value={currentItemOption}
                        onChange={(val) => {
                          const updatedItem = val ? {
                            itemid: val.value,
                            item_full_name: val.label,
                            aguomconval: val.conval || 1,
                            itmhsnId: val.itmhsnId || "",
                            itemuom: val.itemuom || "",
                          } : {
                            itemid: "",
                            item_full_name: "",
                            aguomconval: 1,
                            itmhsnId: "",
                            itemuom: "",
                          };
                          if (fullTableIndex !== -1) {
                            updateTableItem(fullTableIndex, { item: updatedItem });
                          }
                        }}
                        vendorId={vendorId}
                        placeholder="Search and select item..."
                        isDisabled={!vendorId}
                        className="min-w-[250px]"
                      />
                    </TD>

                    {["AGENTBILL"].includes(docType) && (
                      <TD>
                        <Input
                          type="number"
                          value={row.srate}
                          onChange={(e) => {
                            if (fullTableIndex !== -1) {
                              updateTableItem(
                                fullTableIndex,
                                withSyncedRatesFromExclusive(row, e.target.value)
                              );
                            }
                          }}
                          showLabel={false}
                          className="w-24 text-right"
                          min="0"
                          step="0.01"
                        />
                      </TD>
                    )}

                    {["AGENTBILL"].includes(docType) && hasApplicableGst && (
                      <TD>
                        <Input
                          type="number"
                          value={row.srate_incl ?? exclusiveToInclusive(row.srate, row.tax)}
                          onChange={(e) => {
                            if (fullTableIndex !== -1) {
                              updateTableItem(
                                fullTableIndex,
                                withSyncedRatesFromInclusive(row, e.target.value)
                              );
                            }
                          }}
                          showLabel={false}
                          className="w-24 text-right"
                          min="0"
                          step="0.01"
                        />
                      </TD>
                    )}
                    
                    <TD>
                      <Input
                        type="number"
                        value={row.sqty}
                        onChange={(e) => {
                          if (fullTableIndex !== -1) {
                            updateTableItem(fullTableIndex, { sqty: parseFloat(e.target.value) || 0 });
                          }
                        }}
                        showLabel={false}
                        className="w-24"
                        min="0"
                        step="0.001"
                      />
                    </TD>
                    
                    <TD>
                      <span className="text-sm text-gray-900">
                        {row.item.itemuom || '-'}
                      </span>
                    </TD>

                    {["AGENTBILL"].includes(docType) && (
                      <TD>
                        <Input
                          type="number"
                          value={row.dis1}
                          onChange={(e) => {
                            const newDis = parseFloat(e.target.value) || 0;
                            const rate = parseFloat(row.srate) || 0;
                            if (newDis <= rate) {
                              if (fullTableIndex !== -1) {
                                updateTableItem(
                                  fullTableIndex,
                                  withItemDirectDiscount(row, newDis)
                                );
                              }
                            }
                          }}
                          showLabel={false}
                          className="w-24 text-right"
                          min="0"
                          step="0.01"
                        />
                      </TD>
                    )}

                    {["AGENTBILL"].includes(docType) && (
                      <TD>
                        <Input
                          type="number"
                          value={row.dis2perc || 0}
                          onChange={(e) => {
                            if (fullTableIndex !== -1) {
                              updateTableItem(
                                fullTableIndex,
                                withItemDiscountPercentage(row, e.target.value)
                              );
                            }
                          }}
                          showLabel={false}
                          className="w-20 text-right"
                          min="0"
                          max="99"
                          step="0.01"
                        />
                      </TD>
                    )}

                    {["AGENTBILL"].includes(docType) && (
                      <TD className="text-right">
                        {rowAmount}
                      </TD>
                    )}

                    {companyHasGst && (
                      <TD className="text-center">
                        <span className="text-sm text-gray-900">
                          {row.item.itmhsnId || '-'}
                        </span>
                      </TD>
                    )}

                    {["AGENTBILL"].includes(docType) && hasApplicableGst && (
                      <TD className="text-right">
                        {row.tax || 0}%
                      </TD>
                    )}
                    
                    <TD last>
                      <ActionButton
                        onClick={() => {
                          if (fullTableIndex !== -1) {
                            removeTableItem(fullTableIndex);
                          }
                        }}
                        variant="danger"
                        title="Delete"
                        className="mr-0"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </ActionButton>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>
      )}
    </div>
  );
}
